import type { ApprovalRecordDto, AssetDto } from '@nsb/core';
import { useState } from 'react';
import { Link } from 'react-router';
import { ApiError, errorText, queryClient, upload, useApi } from '../../api.ts';
import { dateWords, easternToday } from '../../days.ts';
import { typeOf } from '../../fileTypes.ts';
import { assetsKey } from '../../screens/project/hooks.ts';
import { DropZone } from '../DropZone.tsx';
import { Button, ErrorLine, Field, TextInput } from '../ui.tsx';
import { processAction } from './useProcess.ts';

/**
 * A client's approval of a gate of theirs, given before the portal or outside it, recorded by NSB (design study 24,
 * L-16 A; Jason, 2026-10-08: "this thing should be considered an approval record and requires either a justification
 * note (most risky), an uploaded document ... just do the justification note and file upload now"): the day they gave
 * it, never a day to come, and its evidence, the document that shows it (dropped here into the project's Files, or
 * picked from them) or a note saying how NSB knows, or both. One form, on the gate's panel and on NSB's Decisions page.
 */
export function RecordApproval({ projectId, stepId, onDone, onCancel }: { projectId: string; stepId: string; onDone?: () => void; onCancel: () => void }) {
  return (
    <RecordGiven
      projectId={projectId}
      intro="The client approved it outside the portal. Recorded here, it reads as their approval on the day they gave it, and the record says you recorded it."
      dayLabel="The day they approved it"
      dropLabel="Drop the signed proposal or the email here"
      button="Record their approval"
      onRecord={(input) => processAction(projectId, `/steps/${stepId}/approval`, input)}
      onDone={onDone}
      onCancel={onCancel}
    />
  );
}

/**
 * The form any of the client's approvals given outside the portal is recorded with (a gate's, a pattern language's lock):
 * the day, never one to come, and the document that shows it (`dropLabel` names what that is) or a note, or both.
 * `onRecord` throws a refusal, shown here.
 */
export function RecordGiven({
  projectId,
  intro,
  dayLabel,
  dropLabel,
  button,
  onRecord,
  onDone,
  onCancel,
}: {
  projectId: string;
  intro: string;
  dayLabel: string;
  dropLabel: string;
  button: string;
  onRecord: (input: { on: string; note: string | undefined; fileId: string | null }) => Promise<unknown>;
  onDone?: (() => void) | undefined;
  onCancel: () => void;
}) {
  const { data: files } = useApi<AssetDto[]>(assetsKey(projectId), `/projects/${projectId}/assets`);
  const [on, setOn] = useState(easternToday());
  const [note, setNote] = useState('');
  const [fileId, setFileId] = useState('');
  const [picking, setPicking] = useState(false);
  const [sending, setSending] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const file = (files ?? []).find((f) => f.id === fileId) ?? null;
  const today = easternToday();
  const ready = Boolean(on) && on <= today && (Boolean(file) || Boolean(note.trim()));

  // A document dropped or chosen goes into the project's Files, NSB's alone, as any file added there does.
  const send = async (dropped: File[]) => {
    const [doc] = dropped;
    if (!doc) return;
    setErr(null);
    setSending(`Sending ${doc.name}`);
    try {
      const before = new Set((files ?? []).map((f) => f.id));
      const after = await upload<AssetDto[]>(`/projects/${projectId}/assets?name=${encodeURIComponent(doc.name)}`, doc, typeOf(doc));
      queryClient.setQueryData(assetsKey(projectId), after);
      const added = after.find((f) => !before.has(f.id) && f.mine) ?? after.find((f) => !before.has(f.id));
      if (added) setFileId(added.id);
      setPicking(false);
    } catch (e) {
      setErr(`${doc.name}: ${e instanceof ApiError && e.status === 413 ? 'too big to send. Keep files under 100 MB.' : errorText(e)}`);
    } finally {
      setSending(null);
    }
  };

  const record = async () => {
    setBusy(true);
    setErr(null);
    try {
      await onRecord({ on, note: note.trim() || undefined, fileId: file?.id ?? null });
      onDone?.();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 rounded-[2px] bg-sheet p-3 sm:p-4">
      <p className="mb-3 max-w-measure text-sm">{intro}</p>
      <Field label={dayLabel}>{(id) => <TextInput id={id} type="date" max={today} value={on} onChange={(e) => setOn(e.target.value)} />}</Field>
      <div className="mb-5">
        <p className="label mb-1">The document that shows it</p>
        {file ? (
          <p className="flex flex-wrap items-baseline gap-x-4 text-sm">
            <span className="min-w-0 break-words">{file.name}</span>
            <button type="button" className="link min-h-11 text-ink-3 hover:text-ink" onClick={() => setFileId('')}>
              Take it off
            </button>
          </p>
        ) : picking ? (
          <div>
            <label className="block">
              <span className="sr-only">From the project’s files</span>
              <select className="field" value={fileId} onChange={(e) => setFileId(e.target.value)}>
                <option value="">Choose one of the project’s files</option>
                {(files ?? []).map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="link mt-1 min-h-11 text-sm text-ink-3 hover:text-ink" onClick={() => setPicking(false)}>
              Add a new one instead
            </button>
          </div>
        ) : (
          <DropZone
            compact
            label={dropLabel}
            busy={sending !== null}
            onFiles={(dropped) => void send(dropped)}
            hint={sending ? <span className="motion-safe:animate-pulse">{sending}</span> : 'It goes into the project’s Files, yours alone unless you share it.'}
          >
            {files?.length ? (
              <button type="button" className="link min-h-11 text-sm" onClick={() => setPicking(true)}>
                Pick one from Files
              </button>
            ) : null}
          </DropZone>
        )}
      </div>
      <Field label="Or a note saying how you know" hint="A note alone is the riskiest record: add the document when there is one.">
        {(id) => <textarea id={id} rows={2} maxLength={1000} className="field resize-y" value={note} onChange={(e) => setNote(e.target.value)} />}
      </Field>
      <div className="flex flex-wrap gap-x-5 gap-y-2">
        <Button busy={busy} disabled={!ready || sending !== null} onClick={() => void record()}>
          {button}
        </Button>
        <Button tone="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      <ErrorLine error={err} />
    </div>
  );
}

/** What NSB's panel says of an approval it recorded (L-16 A): the evidence, the note and the document, under who recorded it. */
export function RecordedApproval({ projectId, record: r }: { projectId: string; record: ApprovalRecordDto }) {
  return (
    <div className="mt-1 space-y-1 text-sm text-ink-2">
      {r.note ? <p className="max-w-measure">How you knew: “{r.note}”</p> : null}
      {r.file ? (
        <p>
          The document:{' '}
          <Link to={`/p/${projectId}/reviews/files/${r.file.id}?from=files`} className="link">
            {r.file.name}
          </Link>
        </p>
      ) : null}
      <p className="meta">Recorded {dateWords(r.recordedAt, 'date')}</p>
    </div>
  );
}
